import { useState, useEffect } from 'react';
import { useShippingConfigLoaded } from '@/contexts/ShippingConfigContext';
import { getShippingConfig } from '@/lib/shippingConfig';
import { shippingApi } from '@/api/customer/shipping';
import { useAppConfig } from '@/contexts/AppConfigContext';

interface ShippingCalculationResult {
  cost: number;
  isLoading: boolean;
  isFree: boolean;
  message?: string;
}

/**
 * Función auxiliar para calcular el costo de envío sin hooks
 * Usada por stores y funciones no-React
 */
export const calculateShippingCost = (subtotal: number): number => {
  const config = getShippingConfig();

  if (!config) {
    return -1; // Config no cargada
  }

  try {
    const type = String(config.type || '').toLowerCase();

    // Si hay umbral de envío gratis y el subtotal lo supera
    if (config.freeShippingThreshold && subtotal >= config.freeShippingThreshold) {
      return 0;
    }

    // Si es tarifa plana
    if (type === 'flat_rate' && config.flatRate !== undefined) {
      return config.flatRate;
    }

    // Si es envío gratis
    if (type === 'free') {
      return 0;
    }

    // Usar costo predeterminado como fallback
    return config.defaultCost || 0;

  } catch (error) {
    console.error('❌ Error calculating shipping cost:', error);
    return config.defaultCost || 0;
  }
};

/**
 * Hook para calcular el costo de envío basado en el subtotal y destino
 * Maneja toda la lógica de cálculo y estado de carga
 */
export const useShippingCalculation = (
  subtotal: number,
  destination?: { state?: string; postalCode?: string; city?: string },
  items: Array<{ productId: string; quantity: number }> = []
): ShippingCalculationResult => {
  const [result, setResult] = useState<ShippingCalculationResult>({
    cost: -1,
    isLoading: true,
    isFree: false,
  });
  const { loaded } = useShippingConfigLoaded();
  const app = useAppConfig();

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      const cfg = getShippingConfig();
      if (!loaded || !cfg) {
        if (!cancelled) {
          setResult({
            cost: -1,
            isLoading: true,
            isFree: false,
            message: 'Calculando envío...'
          });
        }
        return;
      }

      const localCfg = getShippingConfig();
      const type = String(localCfg?.type || '').toLowerCase();
      const hasLocalRule = !!localCfg && ((type === 'flat_rate' && typeof localCfg.flatRate === 'number') || (type === 'free') || (typeof localCfg.defaultCost === 'number') || (localCfg.freeShippingThreshold && localCfg.freeShippingThreshold > 0));

      let cost = -1;
      if (hasLocalRule) {
        cost = calculateShippingCost(subtotal);
      } else {
        try {
          // Ensure we always have a valid state to prevent 400 errors
          const defaultState = app.regional?.defaultCountry || 'CO';
          const dest = {
            state: destination?.state || defaultState,
            postalCode: destination?.postalCode || '00000',
            city: destination?.city || 'N/A'
          };

          const resp = await shippingApi.calculate({
            subtotal,
            destination: dest,
            items: items
          });
          cost = resp.cost;
        } catch (e) {
          console.error('❌ Error calculating shipping API:', e);
          const defaultCost = typeof localCfg?.defaultCost === 'number' && (localCfg!.defaultCost as number) >= 0 ? (localCfg!.defaultCost as number) : undefined;
          const flatRate = typeof localCfg?.flatRate === 'number' && (localCfg!.flatRate as number) >= 0 ? (localCfg!.flatRate as number) : undefined;
          cost = (defaultCost ?? flatRate ?? -1);
        }
      }
      const isFree = cost === 0;

      let message = '';
      if (cost === -1) {
        message = 'Calculando envío...';
      } else if (isFree) {
        message = '¡Envío GRATIS!';
      } else {
        message = 'Tarifa de envío';
      }

      if (!cancelled) {
        setResult({
          cost,
          isLoading: false,
          isFree,
          message
        });
      }
    }, 500); // Debounce 500ms

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [subtotal, loaded, app.regional, destination?.state, destination?.postalCode, destination?.city, JSON.stringify(items)]);

  return result;
};

/**
 * Hook para obtener el umbral de envío gratis
 */
export const useFreeShippingThreshold = (): number => {
  const config = getShippingConfig();
  return config?.freeShippingThreshold ?? 0;
};